import { useEffect, useRef, useState } from "react";

import { POLL } from "@/lib/format";

/** The hardened cursor-tail poller (U1) — every live console uses THIS, never a
 *  hand-rolled interval. Carries all five incident-derived properties:
 *
 *  1. SINGLE-FLIGHT — a tick that fires while the previous fetch is
 *     outstanding skips (overlapping fetches read the same cursor and append
 *     duplicate batches in response-arrival order; measured: 13 copies of one
 *     record, interleaved, when scrolling delayed the main thread).
 *  2. HIDDEN-TAB GUARD — no polling while `document.hidden`; a
 *     visibilitychange listener fires an immediate catch-up tick on return
 *     (background tabs must not churn CPU/heap for hours).
 *  3. CURSOR CATCH-UP — fetches are incremental since the last server-assigned
 *     cursor; nothing is lost across pauses/hidden periods (server-side data).
 *  4. DEDUPE ON APPEND — duplicates are structurally impossible, not unlikely.
 *  5. BOUNDED BUFFER — the window never exceeds `max`; a tab left open
 *     overnight reaches a memory ceiling, not a limit of patience.
 */
export function useCursorTail<T>(opts: {
  /** Incremental fetch: items strictly after `cursor` (undefined = initial fill). */
  fetch: (cursor: string | number | undefined) => Promise<T[]>;
  /** Server-assigned monotone cursor of an item (seq / id / ts-cursor). */
  cursorOf: (item: T) => string | number;
  /** Buffer cap (sliding window). */
  max: number;
  /** Poll cadence — default POLL.CONSOLE (2s). */
  intervalMs?: number;
  /** Freeze the stream (view state only; the server keeps the history). */
  paused?: boolean;
  /** Changing this clears the buffer and restarts from the initial fill. */
  resetKey?: unknown;
}) {
  const { fetch, cursorOf, max, intervalMs = POLL.CONSOLE, paused = false, resetKey } = opts;
  const [items, setItems] = useState<T[]>([]);
  const cursor = useRef<string | number | undefined>(undefined);
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;                 // latest closure without re-arming the effect

  useEffect(() => {
    setItems([]);
    cursor.current = undefined;
  }, [resetKey]);

  useEffect(() => {
    if (paused) return;
    let alive = true;
    const busy = { current: false };
    const tick = async () => {
      if (busy.current || document.hidden) return;
      busy.current = true;
      try {
        const batch = await fetchRef.current(cursor.current);
        if (!alive || !batch.length) return;
        cursor.current = cursorOf(batch[batch.length - 1]);
        setItems((prev) => {
          const seen = new Set(prev.map(cursorOf));
          const fresh = batch.filter((x) => !seen.has(cursorOf(x)));
          return fresh.length ? [...prev, ...fresh].slice(-max) : prev;
        });
      } catch {
        /* transient — next tick retries */
      } finally {
        busy.current = false;
      }
    };
    void tick();
    const t = setInterval(tick, intervalMs);
    const onVis = () => { if (!document.hidden) void tick(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      alive = false;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, resetKey, intervalMs, max]);

  const clear = () => {
    setItems([]);
    cursor.current = undefined;
  };
  /** For consumers that clear server-side state too (cursor restarts at 0). */
  return { items, clear };
}
